import { useToast } from "@/hooks/use-toast";
import { useEffect, useRef, useState } from "react";
import type React from "react";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Check, Loader2, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MarketPdfExtractionJobStatus, MarketPdfImportResult, MarketPdfPreview } from "./types";

// Poll interval for the background extraction job. A 10-page Gemini vision
// batch takes tens of seconds, so 2s keeps the progress bar lively without
// hammering the server.
const POLL_INTERVAL_MS = 2000;

export function MarketResearchPdfImportPanel() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [fileName, setFileName] = useState<string>("");
  const [preview, setPreview] = useState<MarketPdfPreview | null>(null);
  const [editableMonth, setEditableMonth] = useState<string>("");
  const [refreshAnalyses, setRefreshAnalyses] = useState<boolean>(true);
  const [importResult, setImportResult] = useState<MarketPdfImportResult | null>(null);
  // Task #3660 — Background extraction job state. Set when an upload is
  // enqueued; cleared once the preview lands (or the job fails).
  const [jobStatus, setJobStatus] = useState<MarketPdfExtractionJobStatus | null>(null);
  const jobId = jobStatus && jobStatus.state === "processing" ? jobStatus.jobId : null;

  function readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error);
      reader.onload = () => resolve(String(reader.result));
      reader.readAsDataURL(file);
    });
  }

  const parseMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (file: File) => {
      if (file.type !== "application/pdf") {
        throw new Error("Please upload a PDF file");
      }
      // Base64 expands payload by ~33%, so a 14MB raw PDF arrives at
      // the server as ~19MB — under the 20MB express.json limit on
      // /api/admin/market-research/parse-pdf.
      if (file.size > 14 * 1024 * 1024) {
        throw new Error("PDF is too large (max 14 MB)");
      }
      const dataUrl = await readFileAsDataUrl(file);
      // Task #3660 — The server now enqueues a background extraction and
      // returns a job id immediately; the polling effect below tracks
      // progress and surfaces the preview when the job completes.
      const res = await apiRequest("POST", "/api/admin/market-research/parse-pdf", {
        dataUrl,
        fileName: file.name,
      });
      return (await res.json()) as MarketPdfExtractionJobStatus;
    },
    onSuccess: (data) => {
      setPreview(null);
      setImportResult(null);
      setJobStatus(data);
    },
    onError: (err: Error) => {
      setJobStatus(null);
      toast({
        title: "Failed to parse PDF",
        description: parseErrorMessage(err, "Unknown error"),
        variant: "destructive",
      });
    },
  });

  // Task #3660 — Poll the background extraction job until it completes or
  // fails. On completion we set the preview (the import step); on failure
  // we surface the same specific extractor error the synchronous path used
  // to throw (truncated / stopped early / timed out / per-batch warnings).
  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    const timer = setInterval(async () => {
      try {
        const res = await apiRequest("GET", `/api/admin/market-research/parse-pdf/${jobId}`);
        const status = (await res.json()) as MarketPdfExtractionJobStatus;
        if (cancelled) return;
        if (status.state === "completed" && status.result) {
          setJobStatus(null);
          setPreview(status.result);
          setEditableMonth(status.result.dataMonth || "");
          toast({
            title: "PDF parsed",
            description: `Extracted ${status.result.cities.length} cities${status.result.dataMonth ? ` for ${status.result.dataMonth}` : ""}.`,
          });
        } else if (status.state === "failed") {
          setJobStatus(null);
          toast({
            title: "Failed to parse PDF",
            description: status.error || "Unknown error",
            variant: "destructive",
          });
        } else {
          setJobStatus(status);
        }
      } catch (err) {
        if (cancelled) return;
        setJobStatus(null);
        toast({
          title: "Failed to parse PDF",
          description: parseErrorMessage(err as Error, "Lost contact with the extraction job"),
          variant: "destructive",
        });
      }
    }, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [jobId, toast]);

  const importMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      if (!preview) throw new Error("No preview to import");
      const month = editableMonth.trim();
      if (!month) throw new Error("Data month is required");
      const res = await apiRequest("POST", "/api/admin/market-research/import", {
        dataMonth: month,
        cities: preview.cities,
        refreshAnalyses,
      });
      return (await res.json()) as MarketPdfImportResult;
    },
    onSuccess: (data) => {
      setImportResult(data);
      queryClient.invalidateQueries({ queryKey: ["/api/app/market-research"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/market-research/history"] });
      toast({
        title: "Import complete",
        description: `Upserted ${data.snapshotInserted + data.snapshotUpdated} snapshot rows · inserted ${data.historyInserted} history rows · skipped ${data.historySkipped}.`,
      });
    },
    onError: (err: Error) => {
      toast({
        title: "Import failed",
        description: parseErrorMessage(err, "Unknown error"),
        variant: "destructive",
      });
    },
  });

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setPreview(null);
    setImportResult(null);
    parseMutation.mutate(file);
  }

  function resetPanel() {
    setPreview(null);
    setImportResult(null);
    setEditableMonth("");
    setFileName("");
    setJobStatus(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  return (
    <div className="space-y-4" data-testid="panel-market-research-pdf">
      <div>
        <h3 className="text-lg font-semibold" data-testid="text-market-research-pdf-title">
          Market Research PDF Import
        </h3>
        <p className="text-sm text-muted-foreground">
          Upload the monthly PadSplit Market Spot PDF. Gemini extracts every
          city row, you confirm, and the system upserts <code>market_research</code>{" "}
          plus a fresh row in <code>market_research_history</code> for the month.
          Optionally triggers a per-city Gemini analysis refresh so the public
          Market Research page reflects the new numbers without a redeploy or
          backfill script.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          onChange={onFileChange}
          className="text-sm"
          disabled={parseMutation.isPending || importMutation.isPending}
          data-testid="input-market-research-pdf-file"
        />
        {fileName ? (
          <span className="text-sm text-muted-foreground" data-testid="text-market-research-pdf-filename">
            {fileName}
          </span>
        ) : null}
        {preview ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={resetPanel}
            disabled={importMutation.isPending}
            data-testid="button-market-research-pdf-reset"
          >
            <X className="h-4 w-4 mr-1" />
            Clear
          </Button>
        ) : null}
      </div>

      {parseMutation.isPending || jobStatus ? (
        <Card className="p-4 space-y-2" data-testid="card-market-research-pdf-parsing">
          <div className="flex items-center gap-2 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span data-testid="text-market-research-pdf-parsing-status">
              {jobStatus
                ? jobStatus.totalBatches && jobStatus.totalBatches > 1
                  ? `Extracting in the background — processed ${jobStatus.completedBatches} of ${jobStatus.totalBatches} page batches${jobStatus.pageCount ? ` (${jobStatus.pageCount} pages)` : ""}.`
                  : "Extracting in the background with Gemini…"
                : "Uploading PDF…"}
            </span>
          </div>
          {jobStatus && jobStatus.totalBatches && jobStatus.totalBatches > 1 ? (
            <>
              <div className="h-1.5 w-full overflow-hidden rounded bg-muted">
                <div
                  className="h-full rounded bg-brand transition-all"
                  style={{
                    width: `${Math.min(100, Math.round((jobStatus.completedBatches / jobStatus.totalBatches) * 100))}%`,
                  }}
                  data-testid="bar-market-research-pdf-progress"
                />
              </div>
              <p className="text-[11px] text-muted-foreground">
                Large PDFs run server-side so you can leave this tab open without
                the upload timing out. The preview appears here when extraction
                finishes.
              </p>
            </>
          ) : (
            <p className="text-[11px] text-muted-foreground">
              This typically takes 20-60 seconds for a 100-city report.
            </p>
          )}
        </Card>
      ) : null}

      {preview ? (
        <Card className="p-4 space-y-3" data-testid="card-market-research-pdf-preview">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[180px]">
              <label className="text-xs font-medium text-muted-foreground mb-1 block">
                Data month
              </label>
              <Input
                value={editableMonth}
                onChange={(e) => setEditableMonth(e.target.value)}
                placeholder="May 2026"
                data-testid="input-market-research-pdf-month"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Use the canonical <code>Month YYYY</code> format (e.g. "May 2026")
                so history rows stay consistent across imports. Abbreviations
                and 2-digit years (e.g. "Jul 26") are normalized automatically.
              </p>
            </div>
            <div className="text-sm text-muted-foreground">
              <span data-testid="text-market-research-pdf-city-count">
                {preview.cities.length}
              </span>{" "}
              cities extracted
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={refreshAnalyses}
                onChange={(e) => setRefreshAnalyses(e.target.checked)}
                data-testid="checkbox-market-research-pdf-refresh"
              />
              Refresh AI analyses for imported cities after import
            </label>
            <Button
              onClick={() => importMutation.mutate()}
              disabled={importMutation.isPending || preview.cities.length === 0 || !editableMonth.trim()}
              data-testid="button-market-research-pdf-import"
            >
              {importMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Confirm import
            </Button>
          </div>

          {preview.warnings.length > 0 ? (
            <div className="rounded border border-yellow-300 bg-yellow-50 dark:bg-yellow-950/30 p-3 text-xs text-yellow-900 dark:text-yellow-200 space-y-1">
              {preview.warnings.map((w, i) => (
                <div key={i} data-testid={`text-market-research-pdf-warning-${i}`}>• {w}</div>
              ))}
            </div>
          ) : null}

          <div className="max-h-[480px] overflow-auto border rounded">
            <Table data-testid="table-market-research-pdf-preview">
              <TableHeader>
                <TableRow>
                  <TableHead>City</TableHead>
                  <TableHead>ST</TableHead>
                  <TableHead className="text-right">Occ %</TableHead>
                  <TableHead className="text-right">Shared $</TableHead>
                  <TableHead className="text-right">Private $</TableHead>
                  <TableHead className="text-right">Searches</TableHead>
                  <TableHead className="text-right">Units</TableHead>
                  <TableHead className="text-right">Upcoming</TableHead>
                  <TableHead className="text-right">Available</TableHead>
                  <TableHead className="text-right">S:R</TableHead>
                  <TableHead className="text-right">DTFB</TableHead>
                  <TableHead className="text-right">DT80</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.cities.map((c, i) => (
                  <TableRow key={`${c.city}-${c.state}-${i}`} data-testid={`row-market-research-pdf-${i}`}>
                    <TableCell className="font-medium">{c.city}</TableCell>
                    <TableCell>{c.state}</TableCell>
                    <TableCell className="text-right">{c.occupancyRate ?? "—"}</TableCell>
                    <TableCell className="text-right">{c.avgPriceShared ?? "—"}</TableCell>
                    <TableCell className="text-right">{c.avgPricePrivate ?? "—"}</TableCell>
                    <TableCell className="text-right">{c.totalSearchVolume ?? "—"}</TableCell>
                    <TableCell className="text-right">{c.totalUnits ?? "—"}</TableCell>
                    <TableCell className="text-right">{c.upcomingUnits ?? "—"}</TableCell>
                    <TableCell className="text-right">{c.availableUnits ?? "—"}</TableCell>
                    <TableCell className="text-right">{c.searchToRoomRatio ?? "—"}</TableCell>
                    <TableCell className="text-right">{c.daysToFirstBooking ?? "—"}</TableCell>
                    <TableCell className="text-right">{c.daysTo80Occupancy ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      ) : null}

      {importResult ? (
        <Card className="p-4 space-y-2" data-testid="card-market-research-pdf-result">
          <div className="text-sm font-semibold">
            Imported {importResult.dataMonth}
          </div>
          <div className="text-sm text-muted-foreground space-y-0.5">
            <div data-testid="text-market-research-pdf-result-snapshot">
              Snapshot rows: {importResult.snapshotInserted} inserted ·{" "}
              {importResult.snapshotUpdated} updated
            </div>
            <div data-testid="text-market-research-pdf-result-history">
              History rows: {importResult.historyInserted} inserted ·{" "}
              {importResult.historySkipped} skipped (already exist for this month)
            </div>
            {importResult.refreshTriggered > 0 ? (
              <div data-testid="text-market-research-pdf-result-refresh">
                AI analysis refresh queued for {importResult.refreshTriggered} cities.
                Each call takes ~15-20s; the City AI Analyses tab will reflect new
                rows as they land.
              </div>
            ) : null}
          </div>
          {importResult.failedCities.length > 0 ? (
            <div className="rounded border border-destructive/30 bg-destructive/5 p-3 text-xs space-y-1">
              <div className="font-semibold text-destructive">
                {importResult.failedCities.length} cities failed:
              </div>
              {importResult.failedCities.map((f, i) => (
                <div key={i} data-testid={`text-market-research-pdf-failed-${i}`}>
                  • {f.city}, {f.state}: {f.error}
                </div>
              ))}
            </div>
          ) : null}
        </Card>
      ) : null}
    </div>
  );
}
